'use client'

import { useEffect } from 'react'
import ReactConfetti from 'react-confetti'
import { CheckCircle, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

export type ShareRoomSuccessOverlayProps = {
  confettiActive: boolean
  confirmationOpen: boolean
  confirmationMessage: string
  /** Full teardown: hide dialog + confetti + notify parent (e.g. `onClose` on share modal). */
  onConfirmationClose: () => void
  /** Stops confetti animation after the same 5s window as the legacy modal. */
  onConfettiFade: () => void
}

export function ShareRoomSuccessOverlay({
  confettiActive,
  confirmationOpen,
  confirmationMessage,
  onConfirmationClose,
  onConfettiFade,
}: ShareRoomSuccessOverlayProps) {
  useEffect(() => {
    if (!confettiActive) return
    const timer = setTimeout(() => {
      onConfettiFade()
    }, 5000)
    return () => clearTimeout(timer)
  }, [confettiActive, onConfettiFade])

  return (
    <>
      {confettiActive && (
        <div className="pointer-events-none fixed inset-0 z-[60]">
          <ReactConfetti
            width={window.innerWidth}
            height={window.innerHeight}
            recycle={false}
            numberOfPieces={200}
          />
        </div>
      )}

      <Dialog
        open={confirmationOpen}
        onOpenChange={(open) => {
          if (!open) onConfirmationClose()
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <CheckCircle className="mr-2 h-6 w-6 text-green-500" />
              Success
            </DialogTitle>
          </DialogHeader>
          <DialogDescription>{confirmationMessage}</DialogDescription>
          <Button onClick={onConfirmationClose} className="mt-4">
            Close
          </Button>
          <button
            type="button"
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
            onClick={onConfirmationClose}
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        </DialogContent>
      </Dialog>
    </>
  )
}
